import { Locales } from '@magicdoorinc/date';
import { useLocalization, IconKeyRound, IconLogOut, IconSettings, Popover, Avatar, Button } from '@magicdoorinc/ui';
import { For, Show, createMemo, createSignal } from 'solid-js';
import IconBell from '~/assets/images/common/bell.svg?component-solid';
import IconDown from '~/assets/images/common/down.svg?component-solid';
import { CompanyTypeBadge } from '~/components/auth/CompanyTypeBadge';
import { getSignedInAccounts, addAccount, routeToBusinessModeHostAfterSwitch } from '~/config/auth';
import { useAuth, useCurrentCompany, usePropertyManagerPermissions } from '~/contexts/global';
import { CompanyBusinessMode } from '~/swagger/portal/Api';
import { getCrossHostAccounts, crossHostAccountUrl } from '~/utils/crossHostAccounts';
import { showRedirectNotice } from '~/utils/hoaDemoMode';
import { loadLocale } from '~/utils/i18n';
import type { SignedInAccount } from '~/config/auth';
import type { CrossHostAccount } from '~/utils/crossHostAccounts';

type SwitchableAccount = {
  label: string;
  subLabel: string;
  businessMode: CompanyBusinessMode | undefined;
  onSelect: () => void;
};

const COLLAPSED_ACCOUNT_COUNT = 3;

// Languages are listed in their own language (never translated), so a user who
// has the portal in a language they can't read still recognizes their own.
const LANGUAGE_OPTIONS: Array<{ id: Locales; label: string }> = [
  { id: Locales.EN_US, label: 'English' },
  { id: Locales.ES_MX, label: 'Español' },
  { id: Locales.ZH_CN, label: '中文' },
];

const ProfileButton = () => {
  const { t, changeLanguage, currentLanguage } = useLocalization();
  const { me, signOut, signOutAll, switchToAccount, activeAccountId } = useAuth();
  const { currentCompany } = useCurrentCompany();
  const { hasPermission } = usePropertyManagerPermissions();

  const handleSwitch = async (account: SignedInAccount) => {
    await switchToAccount(account.id);
    const handedOff = await routeToBusinessModeHostAfterSwitch(account.businessMode);
    if (!handedOff) window.location.reload();
  };

  // activeAccountId() is the reactive trigger; re-read accounts on each change.
  const accounts = createMemo(() => {
    activeAccountId();
    return getSignedInAccounts();
  });
  // Accounts other than the one currently active in this tab.
  const otherAccounts = createMemo(() => accounts().filter((a) => !a.isActive));
  // Accounts signed in on the OTHER portal host (separate origin, so they can't
  // be switched to in place). Clicking one bounces to that host.
  const crossHostAccounts = createMemo(() => getCrossHostAccounts(new Set(accounts().map((a) => a.companyId))));

  const handleCrossHostSwitch = (account: CrossHostAccount) => {
    // Show the same "Redirecting to <portal>… 3 2 1" countdown the login handoff
    // uses, then navigate to the other host's switch route (which activates this
    // account on arrival). Hand over this host's accounts so the other host can
    // show them back to us even without the shared cookie (localhost).
    showRedirectNotice(account.businessMode === CompanyBusinessMode.Hoa, crossHostAccountUrl(account, accounts()));
  };

  // Local and cross-host rows look identical and differ only in what clicking
  // does, so they share one list — the collapse below has to cap the combined
  // count, not each list separately.
  const switchableAccounts = createMemo<SwitchableAccount[]>(() => [
    ...otherAccounts().map((account) => ({
      label: account.label,
      subLabel: account.subLabel,
      businessMode: account.businessMode,
      onSelect: () => void handleSwitch(account),
    })),
    ...crossHostAccounts().map((account) => ({
      label: account.label,
      subLabel: account.subLabel,
      businessMode: account.businessMode,
      onSelect: () => handleCrossHostSwitch(account),
    })),
  ]);

  // Past a few accounts the switcher crowds out everything below it, so the
  // "Switch account" header doubles as a toggle for the whole list — open by
  // default, collapsible when the list gets long. At or under the threshold the
  // list is short enough to always show, and the header is just a label.
  const [expanded, setExpanded] = createSignal(true);
  const isCollapsible = createMemo(() => switchableAccounts().length > COLLAPSED_ACCOUNT_COUNT);
  const listVisible = createMemo(() => !isCollapsible() || expanded());

  return (
    <Popover>
      <Popover.Trigger class="flex items-center gap-1 rounded-full border border-separator px-2 py-1 text-sm font-medium uppercase text-[#1C1C1C] transition-colors aria-expanded:border-primary aria-expanded:bg-primary/5 hover-allowed:hover:bg-primary/5">
        <div class="relative right-0.5 size-7 overflow-hidden rounded-full">
          <Avatar
            loading={me.loading}
            imgUrl={me()?.avatar}
            name={me()?.name ?? '-'}
            class="absolute left-1/2 top-1/2 size-8 -translate-x-1/2 -translate-y-1/2"
          />
        </div>
        <IconDown class="size-3" />
      </Popover.Trigger>
      <Popover.Content
        as="ul"
        class="thinscroll my-1 max-h-[calc(100dvh-5rem)] min-w-64 overflow-y-auto overscroll-contain rounded-md border border-separator bg-white text-sm text-text-02 shadow-lg">
        {/* Active account header */}
        <li class="line-clamp-2 flex flex-col items-center border-b p-5">
          <Avatar loading={me.loading} imgUrl={me()?.avatar} name={me()?.name ?? '-'} class="my-4 size-16" />
          <h3 class="text-sm font-semibold">{me()?.name}</h3>
          <span class="text-xs">{me()?.email}</span>
          <Show when={currentCompany()?.name}>
            <span class="mt-1 flex items-center gap-2">
              <span class="text-text-level03 text-xs">{currentCompany()?.name}</span>
              <CompanyTypeBadge businessMode={currentCompany()?.businessMode} />
            </span>
          </Show>
        </li>

        {/* Switch account — local (instant) and other-host (navigates) accounts together */}
        <Show when={switchableAccounts().length}>
          <Show
            when={isCollapsible()}
            fallback={<li class="text-text-level03 border-b px-4 pb-1 pt-2 text-xs uppercase">{t('Switch account')}</li>}>
            <li class="border-b">
              <button
                type="button"
                class={`text-text-level03 flex w-full items-center justify-between gap-2 px-4 pt-2 text-left text-xs uppercase transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary ${expanded() ? 'pb-1' : 'pb-2'}`}
                aria-expanded={expanded()}
                onClick={() => setExpanded(!expanded())}>
                <span>{t('Switch account')}</span>
                <span class="flex items-center gap-1 normal-case">
                  <Show when={!expanded()}>{switchableAccounts().length}</Show>
                  <IconDown class={`size-3 transition-transform ${expanded() ? 'rotate-180' : ''}`} />
                </span>
              </button>
            </li>
          </Show>
          <For each={listVisible() ? switchableAccounts() : []}>
            {(account) => (
              <Popover.Trigger as="li" class="border-b last:border-b-0">
                <button
                  type="button"
                  class="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left normal-case transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary"
                  disabled={switchToAccount.loading}
                  onClick={account.onSelect}>
                  <span class="flex min-w-0 items-center gap-2.5">
                    <Avatar name={account.label} class="size-7 shrink-0" />
                    <span class="flex min-w-0 flex-col">
                      <span class="flex min-w-0 items-center gap-2">
                        <span class="truncate font-medium">{account.label}</span>
                        <CompanyTypeBadge businessMode={account.businessMode} />
                      </span>
                      <Show when={account.subLabel}>
                        <span class="text-text-level03 truncate text-xs">{account.subLabel}</span>
                      </Show>
                    </span>
                  </span>
                </button>
              </Popover.Trigger>
            )}
          </For>
        </Show>

        {/* Add account */}
        <Popover.Trigger as="li" class="border-b">
          <button
            type="button"
            class="flex w-full items-center gap-2 px-4 py-2.5 text-left normal-case transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary"
            onClick={() => addAccount()}>
            <span class="flex size-7 shrink-0 items-center justify-center rounded-full border border-separator text-base leading-none">
              +
            </span>
            {t('Add account')}
          </button>
        </Popover.Trigger>

        {/* Language — a segmented row rather than one menu item per language, so a
            three-option setting doesn't outweigh the actions around it. Not a
            Popover.Trigger: switching language should leave the menu open so the
            change is visible immediately. */}
        <li class="border-b px-4 pb-2.5 pt-2">
          <span class="text-text-level03 text-xs uppercase">{t('Language')}</span>
          <div class="mt-1.5 flex items-center gap-1 rounded-md bg-grey-light p-0.5">
            <For each={LANGUAGE_OPTIONS}>
              {(language) => (
                <button
                  type="button"
                  class="flex-1 rounded px-2 py-1 text-xs font-medium normal-case transition-colors aria-pressed:bg-white aria-pressed:text-primary aria-pressed:shadow-sm hover-allowed:hover:text-primary"
                  aria-pressed={currentLanguage() === language.id}
                  onClick={() => void loadLocale(language.id).then(() => changeLanguage(language.id))}>
                  {language.label}
                </button>
              )}
            </For>
          </div>
        </li>

        {/* Settings + Notifications + Sign out */}
        <Popover.Trigger as="li" class="p-2">
          <Button
            type="button"
            class="flex w-full items-center justify-start gap-2 rounded-md bg-white px-3 py-2 text-sm normal-case text-inherit transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary"
            href="/settings/account/information"
            variant="text">
            <IconSettings class="size-4" /> {t('Settings')}
          </Button>
          <Button
            type="button"
            class="flex w-full items-center justify-start gap-2 rounded-md bg-white px-3 py-2 text-sm normal-case text-inherit transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary"
            href="/settings/account/notifications"
            variant="text">
            <IconBell class="size-4" /> {t('Notifications')}
          </Button>
          <Show when={hasPermission('api_keys', 'read')}>
            <Button
              type="button"
              class="flex w-full items-center justify-start gap-2 rounded-md bg-white px-3 py-2 text-sm normal-case text-inherit transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary"
              href="/settings/account/developer"
              variant="text">
              <IconKeyRound class="size-4" /> {t('Developer')}
            </Button>
          </Show>
          <button
            type="button"
            class="flex w-full items-center justify-start gap-2 rounded-md px-3 py-2 normal-case transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary"
            onClick={signOut}>
            <IconLogOut class="size-4" /> {t('Sign out')}
          </button>
          <Show when={accounts().length > 1}>
            <button
              type="button"
              class="text-text-level03 mt-1 flex w-full items-center justify-start rounded-md border-t border-separator px-3 pb-2 pt-2.5 text-xs normal-case transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary"
              onClick={() => void signOutAll()}>
              <span class="pl-6">{t('Sign out of all accounts')}</span>
            </button>
          </Show>
        </Popover.Trigger>
      </Popover.Content>
    </Popover>
  );
};

export default ProfileButton;
